import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import liff from "../../lib/liff";
import { useTranslation } from "react-i18next";

import StyledButton from "../components/StyledButton";
import { useUrlParams } from "hooks/useUrlParams";

import { COLORS } from "constants/colors";
import HowToUseSlider from "../components/HowToUseSlider";
import AppLayout from "../components/AppLayout";
import { useTheme } from "@emotion/react";
import {
  getRequestFormUrl,
  getGuidelinesUrl,
  howToUseDetailUrls,
} from "constants/links";
import ButtonDialog from "../components/ButtonDialog";
import { useSoulBackend } from "backend";

// Custom hook for guidelines management
const useGuidelines = (soulId, backend) => {
  const [openGuidelineDialog, setOpenGuidelineDialog] = useState(false);
  const [agreeLoading, setAgreeLoading] = useState(false);

  const checkGuidelinesStatus = async () => {
    try {
      const status = await backend.getGuidelinesAgreement(soulId);
      if (status === false) {
        setOpenGuidelineDialog(true);
      }
    } catch (error) {
      console.error("Failed to check guidelines status:", error);
    }
  };

  const handleAgreeGuideline = async () => {
    setAgreeLoading(true);
    try {
      await backend.agreeGuidelines(soulId);
      setOpenGuidelineDialog(false);
    } catch (error) {
      console.error("Failed to agree to guidelines:", error);
    } finally {
      setAgreeLoading(false);
    }
  };

  useEffect(() => {
    if (soulId) {
      checkGuidelinesStatus();
    }
  }, [soulId, backend]);

  return {
    openGuidelineDialog,
    agreeLoading,
    handleAgreeGuideline,
    setOpenGuidelineDialog,
  };
};

// Component for action buttons
const ActionButtons = ({
  t,
  lng,
  onHowToUseDetail,
  onGuideLine,
  onCloseWindow,
  onContact,
  onRequestForm,
  requestFormUrl,
}) => (
  <>
    <StyledButton
      variant="contained"
      sx={{ marginTop: 6 }}
      onClick={onHowToUseDetail}
    >
      {t("common:HowToUse.Detail")}
    </StyledButton>

    <StyledButton
      variant="contained"
      sx={{ marginTop: 4 }}
      onClick={onGuideLine}
    >
      {t("common:HowToUse.GuideLines")}
    </StyledButton>

    <StyledButton
      variant="contained"
      sx={{ marginTop: 4 }}
      onClick={onCloseWindow}
    >
      {t("common:button.back_to_chat")}
    </StyledButton>

    {requestFormUrl && (
      <StyledButton
        variant="contained"
        sx={{ marginTop: 4 }}
        onClick={onRequestForm}
      >
        {t("common:button.request_form")}
      </StyledButton>
    )}

    <StyledButton variant="contained" sx={{ marginTop: 4 }} onClick={onContact}>
      {t("common:HowToUse.Contact")}
    </StyledButton>
  </>
);

// Component for guidelines dialog
const GuidelinesDialog = ({ open, t, guidelineUrl, agreeLoading, onAgree }) => (
  <ButtonDialog
    open={open}
    title={t("common:HowToUse.BeforeService")}
    showCloseButton={false}
    content={
      <>
        <Typography
          sx={(theme) => ({
            textAlign: "center",
            fontSize: 13,
            color: theme.typography.color,
            fontFamily: theme.typography.fontFamily,
            whiteSpace: "pre-line",
          })}
        >
          {t("common:HowToUse.GuidelineNotice")}
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 24,
            marginBottom: 24,
          }}
        >
          <a
            href={guidelineUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: COLORS.SKY_BLUE,
              textDecoration: "none",
              fontWeight: 500,
              fontSize: 15,
            }}
          >
            {t("common:Footer.guidelines")}
          </a>
        </div>
      </>
    }
    rawButtons={[
      <StyledButton
        variant="contained"
        sx={{ width: "100%" }}
        onClick={onAgree}
        disabled={agreeLoading}
      >
        {t("common:button.confirmed")}
      </StyledButton>,
    ]}
    handleClose={() => { }}
  />
);

function HowToUse() {
  const { soulId, lng, contentWork } = useUrlParams();
  const { t } = useTranslation(["common", contentWork]);
  const theme = useTheme();
  const requestFormUrl = getRequestFormUrl(lng);
  const guidelineUrl = getGuidelinesUrl(contentWork, lng);
  const backend = useSoulBackend();

  const { openGuidelineDialog, agreeLoading, handleAgreeGuideline } =
    useGuidelines(soulId, backend);

  // Navigation handlers
  const handleNavigateToHowToUseDetail = () => {
    window.open(howToUseDetailUrls["common"][lng], "_blank");
  };

  const handleNavigateToGuideLine = () => {
    window.open(guidelineUrl, "_blank");
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleNavigateToRequestForm = () => {
    window.open(requestFormUrl, "_blank");
  };

  const handleNavigateToContact = () => {
    window.open("https://line.me/R/ti/p/@314jdaww", "_blank");
  };

  return (
    <AppLayout page="HowToUse">
      <Box
        sx={{
          display: "flex",
          paddingTop: 4,
          paddingBottom: 4,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            marginBottom: 4,
          }}
        >
          <Typography sx={(theme) => ({ ...theme.components.pageHeader })}>
            {t("common:HowToUse.Title")}
          </Typography>
        </Box>

        <HowToUseSlider />

        <ActionButtons
          t={t}
          lng={lng}
          onHowToUseDetail={handleNavigateToHowToUseDetail}
          onGuideLine={handleNavigateToGuideLine}
          onCloseWindow={handleCloseWindow}
          onContact={handleNavigateToContact}
          onRequestForm={handleNavigateToRequestForm}
          requestFormUrl={requestFormUrl}
        />

        <GuidelinesDialog
          open={openGuidelineDialog}
          t={t}
          guidelineUrl={guidelineUrl}
          agreeLoading={agreeLoading}
          onAgree={handleAgreeGuideline}
        />
      </Box>
    </AppLayout>
  );
}

export default HowToUse;
